import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, map, Observable, of, shareReplay, switchMap, throwError } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private oAuth: OAuthService = inject(OAuthService);
    private http: HttpClient = inject(HttpClient);
    private router: Router = inject(Router);

    private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
    isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

    private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
    isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

    headers = new HttpHeaders({
        Authorization: `Bearer ${this.oAuth.getAccessToken()}`
    });

    private _userIdObservable$: BehaviorSubject<string> = new BehaviorSubject<string>('');

    private _avatarUrlObservable$: BehaviorSubject<string> = new BehaviorSubject<string>('');

    isVi$ = this.http
        .get<ViIdentity>(`${environment.baseUrl}/users/v1/users/me?sections=identity`, {
            headers: this.headers
        })
        .pipe(
            shareReplay(),
            map(viIdentity => {
                this._userIdObservable$.next(viIdentity.id);
                this._avatarUrlObservable$.next(viIdentity.avatarLink);
                const identityType = viIdentity.properties.find(prop => prop.name === 'IdentityType');
                return identityType?.value.toLowerCase().startsWith('vi') || identityType?.value.toLowerCase().startsWith('kuma') || false;
            }),
            catchError(err => {
                if (err instanceof HttpErrorResponse && err.status === 401) {
                    return throwError(() => err);
                } else {
                    return of(false);
                }
            })
        );

    constructor() {
        if (environment.authConfig) {
            this.oAuth.configure(environment.authConfig);
        }
        this.oAuth.events.subscribe(() => {
            this.isAuthenticatedSubject$.next(this.oAuth.hasValidAccessToken());
        });
    }

    canActivateProtectedRoutes$: Observable<boolean> = this.isDoneLoading$.pipe(
        filter(done => done),
        switchMap(() => this.isAuthenticated$)
    );

    runInitialLoginSequence(): Promise<void> {
        return this.oAuth
            .loadDiscoveryDocument()
            .then(() => this.oAuth.tryLoginCodeFlow())
            .then(() => {
                if (this.oAuth.hasValidAccessToken()) {
                    return Promise.resolve();
                }
                return Promise.reject();
            })
            .then(() => {
                this.isDoneLoadingSubject$.next(true);
                if (this.oAuth.state && this.oAuth.state !== 'undefined' && this.oAuth.state !== 'null') {
                    let stateUrl = this.oAuth.state;
                    if (!stateUrl.startsWith('/')) {
                        stateUrl = decodeURIComponent(stateUrl);
                    }
                    console.log('Redirecting to stateUrl:', stateUrl);
                    // return;
                    window.location.href = stateUrl;
                }
            })
            .catch(err => {
                if (err) {
                    this.router.navigate(['/error']);
                } else {
                    this.isDoneLoadingSubject$.next(true);
                }
            });
    }

    redirectToIAMLogin(state?: string) {
        this.oAuth.initCodeFlow(state);
    }

    logout() {
        this.oAuth.logOut();
    }

    getUserIdObservable() {
        return this._userIdObservable$;
    }

    getAvatarObservable() {
        return this._avatarUrlObservable$;
    }
}

export interface ViIdentity {
    properties: {
        name: string;
        value: string;
    }[];
    id: string;
    avatarLink: string;
}
